import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { saveDaybook, getCompletedServicesForDaybook } from "../../components/Api/GmsDaybook";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { searchSpaServices } from "../../components/Api/GmsSpaService";
import { searchCustomers } from "../../components/Api/GmsCustomer";
import Select from "react-select";
import {
    Button,
    Card,
    CardBody,
    Container,
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    Table,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
    hasPagePermissionWithAdmin,
    hasEditOrHigherPermission,
    isUserAdmin,
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatLocalDate = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const formatLocalDateTime = (date) => {
    if (!date) return null;
    const ymd = formatLocalDate(date);
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    return `${ymd}T${hh}:${mm}:${ss}`;
};

const formatTimeDisplay = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${String(minutes).padStart(2, "0")} ${ampm}`;
};

// Service type helpers
const getServiceType = (resource) => {
    if (resource.membership_Id) return "Package Series Membership";
    if (resource.package_Id) return "Package Membership";
    return "Standalone Billed Service";
};

const SERVICE_TYPE_CONFIG = {
    "Standalone Billed Service": { label: "Standalone Billed Service", badge: "success", bg: "#d4edda", border: "#28a745", color: "#155724" },
    "Package Membership": { label: "Package Membership", badge: "warning", bg: "#fff3cd", border: "#ffc107", color: "#856404" },
    "Package Series Membership": { label: "Package Series Membership", badge: "info", bg: "#d1ecf1", border: "#17a2b8", color: "#0c5460" },
};

// ─── Discount Calculation Helpers ────────────────────────────────────────────
const calcNetCostFromPercent = (cost, discountPercent) => {
    const pct = parseFloat(discountPercent) || 0;
    const c = parseFloat(cost) || 0;
    const discAmt = (c * pct) / 100;
    // Round to 2 decimal places for consistent display
    const roundedDiscAmt = Math.round(discAmt * 100) / 100;
    return { discountAmount: roundedDiscAmt, netCost: +(c - roundedDiscAmt).toFixed(2) };
};

const calcNetCostFromAmount = (cost, discountAmount) => {
    const amt = parseFloat(discountAmount) || 0;
    const c = parseFloat(cost) || 0;
    const safeAmt = Math.min(Math.max(amt, 0), c);  // cap to cost
    const pct = c > 0 ? (safeAmt / c) * 100 : 0;
    // Round percentage to 2 decimal places matching BillingPage.js
    const roundedPct = Math.round(pct * 100) / 100;
    return { discountPercent: roundedPct, netCost: +(c - safeAmt).toFixed(2) };
};

// Empty entry template
const emptyEntry = {
    _rowId: null,          // internal
    bookingId: null,
    bookingResourceDetailId: null,
    customerId: null,
    customerName: "",
    serviceId: null,
    serviceName: "",
    serviceType: "standalone",
    fromTime: null,
    toTime: null,
    cost: 0,
    discountPercent: 0,
    discountAmount: 0,
    netCost: 0,
    isAdditional: 0,
    notes: "",
    _error: null,
};

let _rowCounter = 0;
const newRowId = () => ++_rowCounter;

// ═══════════════════════════════════════════════════════════════════════════════
export default function AddDaybookPage() {
    const navigate = useNavigate();

    // Permission
    const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
    const hasPermission =
        hasPagePermissionWithAdmin(permissions, "/GmsAddDaybook") ||
        hasPagePermissionWithAdmin(permissions, "/GmsDaybook");
    const hasEditOrHigher =
        hasEditOrHigherPermission(permissions, "/GmsAddDaybook") ||
        hasEditOrHigherPermission(permissions, "/GmsDaybook") ||
        isUserAdmin();

    // Session
    const getUserSession = () => {
        const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const norm = (v) => {
            if (v == null || v === "" || v === "null") return null;
            const n = typeof v === "string" ? parseInt(v, 10) : v;
            return isNaN(n) || n <= 0 ? null : n;
        };
        let businessId = authUser && Object.keys(authUser).length > 0
            ? norm(authUser.companyId)
            : norm(user.user_Company_Id || localStorage.getItem("userCompanyId"));
        let branchId = authUser && Object.keys(authUser).length > 0
            ? norm(authUser.branchId)
            : norm(user.user_Branch_Id || localStorage.getItem("userBranchId"));
        return { userId: authUser.userId || 1, businessId, branchId };
    };
    const session = getUserSession();

    // ── Global form state ──
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [businesses, setBusinesses] = useState([]);
    const [branches, setBranches] = useState([]);
    const [employees, setEmployees] = useState([]);

    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
    const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);

    // ── Entries table ──
    const [entries, setEntries] = useState([]);
    const [saveLoading, setSaveLoading] = useState(false);


    // ── Service selection modal (from booking completed services) ──
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [loadingServices, setLoadingServices] = useState(false);
    const [completedServices, setCompletedServices] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);  // checked rows in modal
    const [serviceSearchText, setServiceSearchText] = useState("");

    // ── Manual service modal ──
    const MANUAL_EMPTY = {
        selectedCustomer: null,
        selectedService: null,
        fromTime: null, toTime: null,
        cost: "", discountPercent: "", discountAmount: "", netCost: "",
        notes: "",
    };
    const [showManualModal, setShowManualModal] = useState(false);
    const [manualEntry, setManualEntry] = useState(MANUAL_EMPTY);
    const [custSearchOpts, setCustSearchOpts] = useState([]);
    const [custSearchLoading, setCustSearchLoading] = useState(false);
    const [svcSearchOpts, setSvcSearchOpts] = useState([]);
    const [svcSearchLoading, setSvcSearchLoading] = useState(false);
    const [manualSearchTimeout, setManualSearchTimeout] = useState(null);

    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!hasPermission) {
            window.location.href = "/Dashboard";
            return;
        }
        loadBusinesses();
    }, []);

    // Auto-fill business/branch from session
    useEffect(() => {
        if (session.businessId && businesses.length > 0) {
            const biz = businesses.find((b) => b.business_Id === session.businessId);
            if (biz) setSelectedBusiness({ value: biz.business_Id, label: biz.business_NameEn || biz.business_Name });
        }
    }, [businesses]);

    useEffect(() => {
        if (selectedBusiness) loadBranches(selectedBusiness.value);
        else { setBranches([]); setSelectedBranch(null); }
    }, [selectedBusiness]);

    useEffect(() => {
        if (session.branchId && branches.length > 0) {
            const br = branches.find((b) => b.branch_Id === session.branchId);
            if (br) setSelectedBranch({ value: br.branch_Id, label: br.branch_NameEn || br.branch_Name });
        }
    }, [branches]);

    useEffect(() => {
        if (selectedBranch) loadEmployees();
        else { setEmployees([]); setSelectedEmployee(null); }
    }, [selectedBranch]);

    // ─────────────────────────────────────────────────────────────────────────
    const loadBusinesses = async () => {
        try {
            const data = await getBusinesses({ business_Active: true });
            setBusinesses(data || []);
        } catch (e) { console.error("Error loading businesses:", e); }
    };

    const loadBranches = async (businessId) => {
        try {
            const data = await getBranches({ branch_Active: true, Business_Id: businessId });
            setBranches(data || []);
        } catch (e) { console.error("Error loading branches:", e); setBranches([]); }
    };

    const loadEmployees = async () => {
        try {
            const filter = {};
            if (selectedBusiness?.value) filter.Employee_Business_Id = selectedBusiness.value;
            if (selectedBranch?.value) filter.Employee_Branch_Id = selectedBranch.value;
            const data = await getEmployees(filter);
            setEmployees(data || []);
        } catch (e) { console.error("Error loading employees:", e); }
    };


    const searchEmployeesManually = async (inputValue) => {
        if (!inputValue || inputValue.length < 3) { setEmployeeSearchOptions([]); return; }
        try {
            setEmployeeSearchLoading(true);
            const resp = await searchEmployees(inputValue, 1, 20, selectedBusiness?.value, selectedBranch?.value);
            setEmployeeSearchOptions(
                resp.items.map((e) => ({ value: e.employee_Id, label: e.employee_NameEn || "N/A", employee: e }))
            );
        } catch (e) { setEmployeeSearchOptions([]); }
        finally { setEmployeeSearchLoading(false); }
    };

    // ─── Open manual service modal ─────────────────────────────────────────
    const openManualModal = () => {
        if (!selectedEmployee) { Swal.fire("Warning", "Please select an employee first.", "warning"); return; }
        if (!selectedDate) { Swal.fire("Warning", "Please select a date first.", "warning"); return; }
        setManualEntry(MANUAL_EMPTY);
        setCustSearchOpts([]);
        setSvcSearchOpts([]);
        setShowManualModal(true);
    };

    const searchCustomersForManual = async (text) => {
        if (!text || text.length < 3) { setCustSearchOpts([]); return; }
        setCustSearchLoading(true);
        try {
            const resp = await searchCustomers(text, 1, 20);
            const items = resp.items || resp || [];
            setCustSearchOpts(items.map((c) => ({
                value: c.customer_Id,
                label: `${c.customer_NameEn || ""} ${c.customer_ContactNo ? `— ${c.customer_ContactNo}` : ""}`.trim(),
                customerId: c.customer_Id,
                customerName: c.customer_NameEn || "",
            })));
        } catch { setCustSearchOpts([]); }
        finally { setCustSearchLoading(false); }
    };

    const searchServicesForManual = async (text) => {
        if (!text || text.length < 2) { setSvcSearchOpts([]); return; }
        setSvcSearchLoading(true);
        try {
            const resp = await searchSpaServices(text, 1, 30);
            const items = resp.items || resp || [];
            setSvcSearchOpts(items.map((s) => ({
                value: s.spaService_Id,
                label: s.spaServiceName_En || s.SpaServiceName_En || `Service #${s.spaService_Id}`,
                cost: s.cost || s.Cost || 0,
                duration: s.duration || s.Duration || 0,
                serviceId: s.spaService_Id,
                serviceName: s.spaServiceName_En || s.SpaServiceName_En || `Service #${s.spaService_Id}`,
            })));
        } catch { setSvcSearchOpts([]); }
        finally { setSvcSearchLoading(false); }
    };

    const updateManual = (field, value) => {
        setManualEntry((prev) => {
            const next = { ...prev, [field]: value };
            // Auto-recalculate net cost
            const cost = parseFloat(next.cost) || 0;
            if (field === "cost" || field === "discountPercent") {
                const pct = parseFloat(next.discountPercent) || 0;
                const disc = (cost * pct) / 100;
                const roundedDisc = Math.round(disc * 100) / 100;
                next.discountAmount = roundedDisc > 0 ? roundedDisc.toFixed(2) : "";
                next.netCost = (cost - roundedDisc).toFixed(2);
            } else if (field === "discountAmount") {
                const disc = Math.min(parseFloat(value) || 0, cost);
                const pct = cost > 0 ? (disc / cost) * 100 : 0;
                const roundedPct = Math.round(pct * 100) / 100;
                next.discountPercent = roundedPct > 0 ? roundedPct.toString() : "";
                next.netCost = (cost - disc).toFixed(2);
            }

            // Auto-calculate toTime if fromTime changes and duration is known
            if (field === "fromTime" && value && next.selectedService?.duration > 0) {
                next.toTime = new Date(value.getTime() + next.selectedService.duration * 60000);
            }
            return next;
        });
    };

    const addManualEntry = () => {
        const { selectedCustomer, selectedService,
            fromTime, toTime, cost, discountPercent, discountAmount, netCost, notes } = manualEntry;
        if (!selectedService) { Swal.fire("Validation", "Please select a service.", "warning"); return; }
        if (!fromTime) { Swal.fire("Validation", "Please select a From time.", "warning"); return; }
        if (!toTime) { Swal.fire("Validation", "Please select a To time.", "warning"); return; }
        if (!cost || parseFloat(cost) <= 0) { Swal.fire("Validation", "Please enter a valid cost.", "warning"); return; }

        const finalNet = parseFloat(netCost) || parseFloat(cost) || 0;

        setEntries((prev) => [...prev, {
            ...emptyEntry,
            _rowId: newRowId(),
            bookingId: null,
            bookingResourceDetailId: null,
            customerId: selectedCustomer?.customerId || null,
            customerName: selectedCustomer?.customerName || "—",
            serviceId: selectedService.serviceId,
            serviceName: selectedService.serviceName,
            serviceType: "Standalone Billed Service",
            fromTime: fromTime.toISOString(),
            toTime: toTime.toISOString(),
            cost: parseFloat(cost) || 0,
            discountPercent: parseFloat(discountPercent) || 0,
            discountAmount: parseFloat(discountAmount) || 0,
            netCost: finalNet,
            isAdditional: true, // Manual entries are additional
            notes: notes || "",
        }]);
        setShowManualModal(false);
    };

    // ─── Open service selection modal (from bookings) ───────────────────────
    const openServiceModal = async () => {
        if (!selectedEmployee) {
            Swal.fire("Warning", "Please select an employee first.", "warning");
            return;
        }
        if (!selectedDate) {
            Swal.fire("Warning", "Please select a date first.", "warning");
            return;
        }

        setShowServiceModal(true);
        setCompletedServices([]);
        setSelectedServices([]);
        setServiceSearchText("");
        setLoadingServices(true);

        try {
            const dateStr = formatLocalDate(selectedDate); // "YYYY-MM-DD"

            // ── Single targeted SP call — no client-side filtering needed ──────
            const rows = await getCompletedServicesForDaybook({
                employeeId: Number(selectedEmployee.value),
                bookingDate: dateStr,
                businessId: selectedBusiness?.value ?? null,
                branchId: selectedBranch?.value ?? null,
            });

            // Map flat SP rows to the shape the modal uses
            const flatServices = (rows || []).map((r) => ({
                _id: `${r.booking_Id}_${r.booking_Resource_Detail_Id}`,
                bookingId: r.booking_Id,
                bookingResourceDetailId: r.booking_Resource_Detail_Id,
                customerId: r.customer_Id,
                customerName: r.customer_Name || `Customer #${r.customer_Id}`,
                serviceId: r.service_Id,
                serviceName: r.service_Name || `Service #${r.service_Id}`,
                packageId: r.package_Id || null,
                membershipId: r.membership_Id || null,
                serviceType: r.service_Type || "Standalone Billed Service",
                fromTime: r.from_Time,
                toTime: r.to_Time,
                cost: r.cost || 0,
                isAdditional: false, // Booked services are not additional
            }));

            setCompletedServices(flatServices);
        } catch (e) {
            console.error("Error fetching completed services:", e);
            Swal.fire("Error", "Failed to load completed services: " + (e.message || e), "error");
        } finally {
            setLoadingServices(false);
        }
    };

    const toggleServiceCheck = (svcId) => {
        setSelectedServices((prev) =>
            prev.includes(svcId) ? prev.filter((id) => id !== svcId) : [...prev, svcId]
        );
    };

    const addSelectedServicesToTable = () => {
        const toAdd = completedServices
            .filter((s) => selectedServices.includes(s._id))
            .map((s) => ({
                ...emptyEntry,
                _rowId: newRowId(),
                bookingId: s.bookingId,
                bookingResourceDetailId: s.bookingResourceDetailId,
                customerId: s.customerId,
                customerName: s.customerName,
                serviceId: s.serviceId,
                serviceName: s.serviceName,
                serviceType: s.serviceType,
                fromTime: s.fromTime,
                toTime: s.toTime,
                cost: s.cost || 0,
                discountPercent: 0,
                discountAmount: 0,
                netCost: s.cost || 0,
            }));

        if (toAdd.length === 0) {
            Swal.fire("Info", "No services selected.", "info");
            return;
        }

        setEntries((prev) => [...prev, ...toAdd]);
        setShowServiceModal(false);
    };

    // ─── Row edit helpers ─────────────────────────────────────────────────────
    const updateEntry = useCallback((rowId, field, value) => {
        setEntries((prev) =>
            prev.map((row) => {
                if (row._rowId !== rowId) return row;
                let updated = { ...row, [field]: value, _error: null };

                if (field === "cost") {
                    const c = parseFloat(value) || 0;
                    const { discountAmount, netCost } = calcNetCostFromPercent(c, updated.discountPercent);
                    updated.discountAmount = discountAmount;
                    updated.netCost = netCost;
                } else if (field === "discountPercent") {
                    const { discountAmount, netCost } = calcNetCostFromPercent(updated.cost, value);
                    updated.discountAmount = discountAmount;
                    updated.netCost = netCost;
                } else if (field === "discountAmount") {
                    // Discount amount has higher priority
                    const { discountPercent, netCost } = calcNetCostFromAmount(updated.cost, value);
                    updated.discountPercent = discountPercent;
                    updated.netCost = netCost;
                }
                return updated;
            })
        );
    }, []);

    const removeEntry = (rowId) => {
        setEntries((prev) => prev.filter((r) => r._rowId !== rowId));
    };

    // ─── Save all entries ─────────────────────────────────────────────────────
    const handleSaveAll = async () => {
        if (!selectedBusiness) { Swal.fire("Error", "Please select a Company.", "error"); return; }
        if (!selectedEmployee) { Swal.fire("Error", "Please select an Employee.", "error"); return; }
        if (entries.length === 0) { Swal.fire("Error", "Please add at least one service entry.", "error"); return; }

        // Validate rows
        let hasError = false;
        const validated = entries.map((row) => {
            if (!row.customerId) return { ...row, _error: "Customer is required." };
            if (!row.serviceId) return { ...row, _error: "Service is required." };
            if (!row.fromTime) return { ...row, _error: "From time is required." };
            if (!row.toTime) return { ...row, _error: "To time is required." };
            if ((row.netCost ?? 0) <= 0) return { ...row, _error: "Net cost must be greater than 0." };
            return row;
        });
        setEntries(validated);
        hasError = validated.some((r) => r._error);
        if (hasError) {
            Swal.fire("Validation Error", "Please fix the errors in the table before saving.", "error");
            return;
        }

        const dateStr = formatLocalDate(selectedDate);

        setSaveLoading(true);
        let successCount = 0;
        const errors = [];

        for (const row of entries) {
            try {
                const fromDate = new Date(dateStr + "T" + (row.fromTime ? new Date(row.fromTime).toTimeString().slice(0, 8) : "00:00:00"));
                const toDate = new Date(dateStr + "T" + (row.toTime ? new Date(row.toTime).toTimeString().slice(0, 8) : "00:00:00"));

                const daybookData = {
                    daybook_Id: 0,
                    business_Id: selectedBusiness.value,
                    branch_Id: selectedBranch?.value || null,
                    employee_Id: selectedEmployee.value,
                    customer_Id: row.customerId,
                    service_Id: row.serviceId,
                    booking_Id: row.bookingId || null,
                    booking_Resource_Detail_Id: row.bookingResourceDetailId || null,
                    transaction_DateTime: formatLocalDateTime(fromDate),
                    end_DateTime: formatLocalDateTime(toDate),
                    cost: row.netCost,           // net cost (after discount)
                    discount_Percent: row.discountPercent || null,
                    discount_Amount: row.discountAmount || null,
                    isAdditional: !!row.isAdditional,
                    notes: row.notes || null,
                    created_By: session.userId,
                    created_Date: new Date().toISOString(),
                };

                await saveDaybook(daybookData);
                successCount++;
            } catch (e) {
                errors.push(`${row.serviceName} (${row.customerName}): ${e.message}`);
            }
        }

        setSaveLoading(false);

        if (errors.length === 0) {
            await Swal.fire("Success", `${successCount} daybook entr${successCount === 1 ? "y" : "ies"} saved successfully.`, "success");
            setEntries([]);   // clear table — stay on page so user can add more
        } else {
            await Swal.fire(
                "Partial Success",
                `${successCount} saved. Errors:\n${errors.join("\n")}`,
                "warning"
            );
            // remove successfully-saved rows, keep the failed ones
            setEntries((prev) => prev.filter((r) => errors.some((e) => e.startsWith(`${r.serviceName} (${r.customerName})`))));
        }
    };

    // ─── Filtered services in modal ───────────────────────────────────────────
    const filteredModalServices = completedServices.filter((s) => {
        if (!serviceSearchText) return true;
        const q = serviceSearchText.toLowerCase();
        return (
            s.serviceName.toLowerCase().includes(q) ||
            s.customerName.toLowerCase().includes(q) ||
            String(s.bookingId).includes(q)
        );
    });

    // ─── Computed ─────────────────────────────────────────────────────────────
    const totalNetCost = entries.reduce((sum, r) => sum + (parseFloat(r.netCost) || 0), 0);

    // ─────────────────────────────────────────────────────────────────────────
    const selectStyles = {
        control: (base) => ({ ...base, minHeight: "36px", fontSize: "13px" }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
    };

    if (!hasPermission) return null;

    return (
        <div className="page-content">
            <style>{`
        .svc-row-standalone    { background: #d4edda; border-left: 4px solid #28a745; }
        .svc-row-package       { background: #fff3cd; border-left: 4px solid #ffc107; }
        .svc-row-package-series{ background: #d1ecf1; border-left: 4px solid #17a2b8; }
        .entry-row-err td { border: 1px solid #dc3545 !important; }
        .net-cost-cell { background: #e8f5e9; font-weight: bold; }
        .action-bar { position: sticky; bottom: 0; background: white; z-index: 100; border-top: 2px solid #e9ecef; padding: 12px 0; margin-top: 16px; }
        .legend-dot { width: 14px; height: 14px; border-radius: 3px; display: inline-block; margin-right: 6px; }
      `}</style>

            <Container fluid>
                <Breadcrumb title="Add Daybook Entry" breadcrumbItem="Day Book" />

                {/* Page title row */}
                <Row className="align-items-center mb-3">
                    <Col>
                        <h4 className="mb-0">Add Daybook Entries</h4>
                        <small className="text-muted">Select an employee and date, then pick completed services to log.</small>
                    </Col>
                    <Col xs="auto">
                        <Button color="secondary" outline size="sm" onClick={() => navigate("/GmsDaybook")}>
                            <i className="bx bx-arrow-back me-1" /> Back to Daybook
                        </Button>
                    </Col>
                </Row>

                {/* Top section: employee / date / business */}
                <Card className="mb-3" style={{ overflow: "visible" }}>
                    <CardBody>
                        <Row>
                            {/* Company */}
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Company <span className="text-danger">*</span></Label>
                                    <Select
                                        value={selectedBusiness}
                                        onChange={(opt) => { setSelectedBusiness(opt); setSelectedBranch(null); setSelectedEmployee(null); }}
                                        options={businesses
                                            .filter((b) => b.business_Active !== false)
                                            .map((b) => ({ value: b.business_Id, label: b.business_NameEn || b.business_Name }))}
                                        placeholder="Select Company"
                                        styles={selectStyles}
                                        isClearable
                                        isDisabled={!!localStorage.getItem("userCompanyId")}
                                    />
                                </FormGroup>
                            </Col>

                            {/* Branch */}
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Branch</Label>
                                    <Select
                                        value={selectedBranch}
                                        onChange={(opt) => { setSelectedBranch(opt); setSelectedEmployee(null); }}
                                        options={branches
                                            .filter((b) => b.branch_Active !== false && (!selectedBusiness || b.branch_Buisness_Id === selectedBusiness.value))
                                            .map((b) => ({ value: b.branch_Id, label: b.branch_NameEn || b.branch_Name }))}
                                        placeholder="Select Branch"
                                        styles={selectStyles}
                                        isClearable
                                        isDisabled={!selectedBusiness || !!localStorage.getItem("userBranchId")}
                                        noOptionsMessage={() => !selectedBusiness ? "Select a company first" : "No branches found"}
                                    />
                                </FormGroup>
                            </Col>

                            {/* Date */}
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Date <span className="text-danger">*</span></Label>
                                    <DatePicker
                                        selected={selectedDate}
                                        onChange={(d) => setSelectedDate(d)}
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        maxDate={new Date()}
                                    />
                                </FormGroup>
                            </Col>

                            {/* Employee */}
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Employee <span className="text-danger">*</span></Label>
                                    <Select
                                        value={selectedEmployee}
                                        onChange={(opt) => { setSelectedEmployee(opt); setEntries([]); }}
                                        options={employeeSearchOptions}
                                        placeholder="Type at least 3 chars to search…"
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                        isLoading={employeeSearchLoading}
                                        noOptionsMessage={() => "Type at least 3 characters"}
                                        loadingMessage={() => "Searching employees…"}
                                        onInputChange={(v) => {
                                            if (searchTimeout) clearTimeout(searchTimeout);
                                            const t = setTimeout(() => searchEmployeesManually(v), 300);
                                            setSearchTimeout(t);
                                            return v;
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="d-flex gap-2 mt-1 flex-wrap">
                            <Button
                                color="primary"
                                onClick={openServiceModal}
                                disabled={!selectedEmployee || !selectedDate}
                            >
                                <i className="bx bx-list-check me-1" />
                                Pick Completed Services
                            </Button>

                            <Button
                                color="success"
                                outline
                                onClick={openManualModal}
                                disabled={!selectedEmployee || !selectedDate}
                                title="Add a service directly from the service catalogue — not linked to a booking"
                            >
                                <i className="bx bx-plus me-1" />
                                Add More Services
                            </Button>

                            {/* Legend */}
                            <div className="d-flex align-items-center ms-3 gap-3" style={{ fontSize: "12px", color: "#6c757d" }}>
                                {Object.entries(SERVICE_TYPE_CONFIG).map(([key, cfg]) => (
                                    <span key={key}>
                                        <span className="legend-dot" style={{ background: cfg.bg, border: `2px solid ${cfg.border}` }} />
                                        {cfg.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* Entries Table */}
                {entries.length > 0 && (
                    <Card className="mb-4">
                        <CardBody>
                            <h6 className="card-title mb-3">
                                Service Entries
                                <Badge color="primary" className="ms-2">{entries.length}</Badge>
                            </h6>
                            <div style={{ overflowX: "auto" }}>
                                <Table bordered size="sm" className="mb-0" style={{ minWidth: "1050px" }}>
                                    <thead className="table-dark">
                                        <tr>
                                            <th style={{ width: "140px" }}>Customer</th>
                                            <th style={{ width: "180px" }}>Service</th>
                                            <th style={{ width: "70px" }}>Type</th>
                                            <th style={{ width: "80px" }}>From</th>
                                            <th style={{ width: "80px" }}>To</th>
                                            <th style={{ width: "90px" }}>Cost</th>
                                            <th style={{ width: "80px" }}>Disc %</th>
                                            <th style={{ width: "90px" }}>Disc Amt</th>
                                            <th style={{ width: "95px" }}>Net Cost</th>
                                            <th style={{ width: "160px" }}>Notes</th>
                                            <th style={{ width: "40px" }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.map((row) => {
                                            const typeCfg = SERVICE_TYPE_CONFIG[row.serviceType] || SERVICE_TYPE_CONFIG["Standalone Billed Service"];
                                            return (
                                                <tr
                                                    key={row._rowId}
                                                    style={{ borderLeft: `4px solid ${typeCfg.border}`, background: typeCfg.bg }}
                                                    className={row._error ? "entry-row-err" : ""}
                                                >
                                                    <td>
                                                        <div style={{ fontSize: "12px", fontWeight: 500 }}>{row.customerName || "—"}</div>
                                                        {row.bookingId && (
                                                            <div style={{ fontSize: "10px", color: "#6c757d" }}>Bkg #{row.bookingId}</div>
                                                        )}
                                                        {row._error && (
                                                            <div className="text-danger" style={{ fontSize: "10px" }}>{row._error}</div>
                                                        )}
                                                    </td>
                                                    <td style={{ fontSize: "12px" }}>{row.serviceName}</td>
                                                    <td>
                                                        <span style={{
                                                            display: "inline-block",
                                                            padding: "2px 7px",
                                                            borderRadius: "10px",
                                                            fontSize: "10px",
                                                            fontWeight: 600,
                                                            background: typeCfg.bg,
                                                            border: `1px solid ${typeCfg.border}`,
                                                            color: typeCfg.color,
                                                            whiteSpace: "nowrap",
                                                        }}>{typeCfg.label}</span>
                                                    </td>
                                                    <td style={{ fontSize: "12px" }}>{formatTimeDisplay(row.fromTime)}</td>
                                                    <td style={{ fontSize: "12px" }}>{formatTimeDisplay(row.toTime)}</td>

                                                    {/* Cost */}
                                                    <td>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            bsSize="sm"
                                                            value={row.cost}
                                                            onChange={(e) => updateEntry(row._rowId, "cost", e.target.value)}
                                                            onFocus={(e) => e.target.select()}
                                                            style={{ fontSize: "12px", padding: "3px 5px", width: "80px" }}
                                                        />
                                                    </td>

                                                    {/* Disc % */}
                                                    <td>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                            bsSize="sm"
                                                            value={row.discountPercent}
                                                            onChange={(e) => updateEntry(row._rowId, "discountPercent", e.target.value)}
                                                            onFocus={(e) => e.target.select()}
                                                            style={{ fontSize: "12px", padding: "3px 5px", width: "70px" }}
                                                            title="Edit Discount % (auto-calculates Discount Amount)"
                                                        />
                                                    </td>

                                                    {/* Disc Amt — priority */}
                                                    <td>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            bsSize="sm"
                                                            value={row.discountAmount}
                                                            onChange={(e) => updateEntry(row._rowId, "discountAmount", e.target.value)}
                                                            onFocus={(e) => e.target.select()}
                                                            style={{
                                                                fontSize: "12px",
                                                                padding: "3px 5px",
                                                                width: "80px",
                                                                borderColor: "#17a2b8",
                                                            }}
                                                            title="Discount Amount has priority — auto-calculates Discount %"
                                                        />
                                                    </td>

                                                    {/* Net Cost (readonly) */}
                                                    <td className="net-cost-cell" style={{ fontSize: "12px", padding: "4px 6px" }}>
                                                        {(row.netCost || 0).toFixed(2)}
                                                    </td>

                                                    {/* Notes */}
                                                    <td>
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            value={row.notes}
                                                            onChange={(e) => updateEntry(row._rowId, "notes", e.target.value)}
                                                            style={{ fontSize: "12px", padding: "3px 5px" }}
                                                            placeholder="Optional notes"
                                                        />
                                                    </td>

                                                    {/* Remove */}
                                                    <td className="text-center">
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => removeEntry(row._rowId)}
                                                            style={{ padding: "2px 6px", fontSize: "12px" }}
                                                            title="Remove"
                                                        >
                                                            ×
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="table-secondary fw-bold">
                                            <td colSpan={8} className="text-end pe-2" style={{ fontSize: "13px" }}>
                                                Total Net Cost:
                                            </td>
                                            <td style={{ fontSize: "13px" }}>{totalNetCost.toFixed(2)}</td>
                                            <td colSpan={2} />
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Empty state */}
                {entries.length === 0 && selectedEmployee && (
                    <div className="text-center text-muted py-5">
                        <i className="bx bx-book-bookmark" style={{ fontSize: "48px" }} />
                        <p className="mt-2">No service entries yet. Click "Pick Completed Services" to add entries.</p>
                    </div>
                )}

                {/* Action bar */}
                <div className="action-bar d-flex justify-content-between align-items-center">
                    <Button color="secondary" outline onClick={() => navigate("/GmsDaybook")}>
                        Cancel
                    </Button>
                    {hasEditOrHigher && entries.length > 0 && (
                        <Button
                            color="success"
                            onClick={handleSaveAll}
                            disabled={saveLoading}
                        >
                            {saveLoading ? (
                                <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
                            ) : (
                                <><i className="bx bx-save me-1" /> Save {entries.length} Entr{entries.length === 1 ? "y" : "ies"}</>
                            )}
                        </Button>
                    )}
                </div>
            </Container>

            {/* ── Service Selection Modal ──────────────────────────────────────────── */}
            <Modal isOpen={showServiceModal} toggle={() => setShowServiceModal(false)} size="xl" scrollable>
                <ModalHeader toggle={() => setShowServiceModal(false)}>
                    <i className="bx bx-list-check me-2 text-primary" />
                    Select Completed Services —{" "}
                    <span className="text-primary">{selectedEmployee?.label}</span>{" "}
                    on <span className="text-primary">{selectedDate ? formatLocalDate(selectedDate) : ""}</span>
                </ModalHeader>

                <ModalBody>
                    {/* Legend */}
                    <div className="d-flex gap-3 mb-3 flex-wrap">
                        {Object.entries(SERVICE_TYPE_CONFIG).map(([key, cfg]) => (
                            <span key={key} className="d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                                <span className="legend-dot" style={{ background: cfg.bg, border: `2px solid ${cfg.border}` }} />
                                <span style={{
                                    display: "inline-block",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                    background: cfg.bg,
                                    border: `1px solid ${cfg.border}`,
                                    color: cfg.color,
                                }}>{cfg.label}</span>
                            </span>
                        ))}
                    </div>

                    {/* Search */}
                    <Input
                        type="text"
                        placeholder="Search by service, customer, or booking ID…"
                        value={serviceSearchText}
                        onChange={(e) => setServiceSearchText(e.target.value)}
                        className="mb-3"
                    />

                    {loadingServices && (
                        <div className="text-center py-4">
                            <span className="spinner-border text-primary" />
                            <p className="mt-2 text-muted">Loading completed services…</p>
                        </div>
                    )}

                    {!loadingServices && filteredModalServices.length === 0 && (
                        <div className="text-center text-muted py-4">
                            <i className="bx bx-search" style={{ fontSize: "36px" }} />
                            <p className="mt-2">
                                {completedServices.length === 0
                                    ? "No completed services found for this employee on the selected date."
                                    : "No services match your search."}
                            </p>
                        </div>
                    )}

                    {!loadingServices && filteredModalServices.length > 0 && (
                        <div style={{ maxHeight: "450px", overflowY: "auto" }}>
                            <Table bordered size="sm">
                                <thead className="table-dark sticky-top">
                                    <tr>
                                        <th style={{ width: "36px" }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedServices.length === filteredModalServices.length && filteredModalServices.length > 0}
                                                onChange={(e) =>
                                                    setSelectedServices(e.target.checked ? filteredModalServices.map((s) => s._id) : [])
                                                }
                                                title="Select all"
                                            />
                                        </th>
                                        <th>Booking ID</th>
                                        <th>Customer</th>
                                        <th>Service</th>
                                        <th>Type</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th>Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredModalServices.map((svc) => {
                                        const typeCfg = SERVICE_TYPE_CONFIG[svc.serviceType] || SERVICE_TYPE_CONFIG["Standalone Billed Service"];
                                        const isChecked = selectedServices.includes(svc._id);
                                        return (
                                            <tr
                                                key={svc._id}
                                                style={{ background: typeCfg.bg, borderLeft: `4px solid ${typeCfg.border}`, cursor: "pointer" }}
                                                onClick={() => toggleServiceCheck(svc._id)}
                                            >
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => toggleServiceCheck(svc._id)}
                                                    />
                                                </td>
                                                <td style={{ fontSize: "12px" }}>#{svc.bookingId}</td>
                                                <td style={{ fontSize: "12px", fontWeight: 500 }}>{svc.customerName}</td>
                                                <td style={{ fontSize: "12px" }}>
                                                    {svc.serviceName}
                                                    {svc.packageName && (
                                                        <div style={{ fontSize: "10px", color: "#6c757d" }}>Pkg: {svc.packageName}</div>
                                                    )}
                                                    {svc.membershipName && (
                                                        <div style={{ fontSize: "10px", color: "#6c757d" }}>Series: {svc.membershipName}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <span style={{
                                                        display: "inline-block",
                                                        padding: "2px 7px",
                                                        borderRadius: "10px",
                                                        fontSize: "10px",
                                                        fontWeight: 600,
                                                        background: typeCfg.bg,
                                                        border: `1px solid ${typeCfg.border}`,
                                                        color: typeCfg.color,
                                                        whiteSpace: "nowrap",
                                                    }}>{typeCfg.label}</span>
                                                </td>
                                                <td style={{ fontSize: "12px" }}>{formatTimeDisplay(svc.fromTime)}</td>
                                                <td style={{ fontSize: "12px" }}>{formatTimeDisplay(svc.toTime)}</td>
                                                <td style={{ fontSize: "12px" }}>{(svc.cost || 0).toFixed(2)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    <div className="me-auto text-muted" style={{ fontSize: "13px" }}>
                        {selectedServices.length} service{selectedServices.length !== 1 ? "s" : ""} selected
                    </div>
                    <Button color="secondary" outline onClick={() => setShowServiceModal(false)}>Cancel</Button>
                    <Button
                        color="primary"
                        onClick={addSelectedServicesToTable}
                        disabled={selectedServices.length === 0}
                    >
                        <i className="bx bx-plus-circle me-1" />
                        Add {selectedServices.length > 0 ? selectedServices.length : ""} Selected
                    </Button>
                </ModalFooter>
            </Modal>
            {/* ── Manual Service Modal ───────────────────────────────────────────── */}
            <Modal isOpen={showManualModal} toggle={() => setShowManualModal(false)} size="lg">
                <ModalHeader toggle={() => setShowManualModal(false)}>
                    <i className="bx bx-plus-circle me-2 text-success" />
                    Add Service —{" "}
                    <span className="text-primary">{selectedEmployee?.label}</span>{" "}
                    on <span className="text-primary">{selectedDate ? formatLocalDate(selectedDate) : ""}</span>
                </ModalHeader>

                <ModalBody>
                    <Row className="g-3">
                        {/* Customer */}
                        <Col md={12}>
                            <FormGroup>
                                <Label>Customer</Label>
                                <Select
                                    options={custSearchOpts}
                                    value={manualEntry.selectedCustomer}
                                    onChange={(opt) => setManualEntry((p) => ({
                                        ...p,
                                        selectedCustomer: opt || null
                                    }))}
                                    onInputChange={(v) => {
                                        if (manualSearchTimeout) clearTimeout(manualSearchTimeout);
                                        const t = setTimeout(() => searchCustomersForManual(v), 300);
                                        setManualSearchTimeout(t);
                                        return v;
                                    }}
                                    isLoading={custSearchLoading}
                                    isClearable
                                    placeholder="Type at least 3 characters to search customers..."
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue.length < 3 ? "Type at least 3 characters" : "No customers found"
                                    }
                                    styles={selectStyles}
                                />
                            </FormGroup>
                        </Col>

                        {/* Service & Times on one row */}
                        <Col md={6}>
                            <FormGroup>
                                <Label>Service <span className="text-danger">*</span></Label>
                                <Select
                                    options={svcSearchOpts}
                                    value={manualEntry.selectedService}
                                    onChange={(opt) => {
                                        setManualEntry((p) => {
                                            const cost = opt?.cost || 0;
                                            const disc = parseFloat(p.discountPercent) || 0;
                                            const discAmt = (cost * disc) / 100;
                                            const newState = {
                                                ...p,
                                                selectedService: opt || null,
                                                cost: cost || "",
                                                netCost: (cost - discAmt).toFixed(2),
                                                discountAmount: discAmt > 0 ? discAmt.toFixed(2) : "",
                                            };
                                            // Auto-calc toTime if fromTime is already set
                                            if (opt?.duration > 0 && p.fromTime) {
                                                newState.toTime = new Date(p.fromTime.getTime() + opt.duration * 60000);
                                            }
                                            return newState;
                                        });
                                    }}
                                    onInputChange={(v) => {
                                        if (manualSearchTimeout) clearTimeout(manualSearchTimeout);
                                        const t = setTimeout(() => searchServicesForManual(v), 300);
                                        setManualSearchTimeout(t);
                                        return v;
                                    }}
                                    isLoading={svcSearchLoading}
                                    isClearable
                                    placeholder="Search service..."
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue.length < 2 ? "Type at least 2 characters" : "No services found"
                                    }
                                    styles={selectStyles}
                                />
                            </FormGroup>
                        </Col>

                        <Col md={3}>
                            <FormGroup>
                                <Label>From <span className="text-danger">*</span></Label>
                                <DatePicker
                                    selected={manualEntry.fromTime}
                                    onChange={(date) => updateManual("fromTime", date)}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption="Time"
                                    dateFormat="h:mm aa"
                                    className="form-control"
                                    placeholderText="From"
                                />
                            </FormGroup>
                        </Col>

                        <Col md={3}>
                            <FormGroup>
                                <Label>To <span className="text-danger">*</span></Label>
                                <DatePicker
                                    selected={manualEntry.toTime}
                                    onChange={(date) => updateManual("toTime", date)}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption="Time"
                                    dateFormat="h:mm aa"
                                    className="form-control"
                                    placeholderText="To"
                                />
                            </FormGroup>
                        </Col>

                        {/* Cost */}
                        <Col md={4}>
                            <FormGroup>
                                <Label>Cost <span className="text-danger">*</span></Label>
                                <Input
                                    type="number" min="0" step="0.01"
                                    placeholder="0.00"
                                    value={manualEntry.cost}
                                    onChange={(e) => updateManual("cost", e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                />
                            </FormGroup>
                        </Col>

                        {/* Discount % */}
                        <Col md={4}>
                            <FormGroup>
                                <Label>Discount %</Label>
                                <Input
                                    type="number" min="0" max="100" step="0.01"
                                    placeholder="0"
                                    value={manualEntry.discountPercent}
                                    onChange={(e) => updateManual("discountPercent", e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                />
                            </FormGroup>
                        </Col>

                        {/* Discount Amount */}
                        <Col md={4}>
                            <FormGroup>
                                <Label>Discount Amount</Label>
                                <Input
                                    type="number" min="0" step="0.01"
                                    placeholder="0.00"
                                    value={manualEntry.discountAmount}
                                    onChange={(e) => updateManual("discountAmount", e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                />
                            </FormGroup>
                        </Col>

                        {/* Net Cost preview */}
                        <Col md={12}>
                            <div style={{
                                background: "#e8f5e9", border: "1px solid #a5d6a7",
                                borderRadius: "6px", padding: "10px 16px",
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}>
                                <span style={{ fontWeight: 600, color: "#2e7d32" }}>Net Cost (after discount)</span>
                                <span style={{ fontSize: "18px", fontWeight: 700, color: "#1b5e20" }}>
                                    {manualEntry.netCost !== "" ? parseFloat(manualEntry.netCost || 0).toFixed(2)
                                        : parseFloat(manualEntry.cost || 0).toFixed(2)}
                                </span>
                            </div>
                        </Col>

                        {/* Notes */}
                        <Col md={12}>
                            <FormGroup>
                                <Label>Notes</Label>
                                <Input
                                    type="textarea"
                                    rows={2}
                                    placeholder="Optional notes…"
                                    value={manualEntry.notes}
                                    onChange={(e) => updateManual("notes", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" outline onClick={() => setShowManualModal(false)}>Cancel</Button>
                    <Button color="success" onClick={addManualEntry}>
                        <i className="bx bx-plus me-1" /> Add to Entry Table
                    </Button>
                </ModalFooter>
            </Modal>

        </div>
    );
}
